import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { Alert } from '@cbm/shared';

interface AlertTrendChartProps {
  alerts: Alert[];
  timeRange?: 'day' | 'week' | 'month';
  chartType?: 'bar' | 'line';
  height?: number;
  className?: string;
}

interface AlertTrendData {
  period: string;
  timestamp: number;
  EARLY: number;
  MID: number;
  LATE: number;
  total: number;
}

// Color mapping for alert severities
const SEVERITY_COLORS = {
  EARLY: '#fbbf24',   // yellow
  MID: '#f97316',     // orange
  LATE: '#dc2626',    // red
};

export function AlertTrendChart({
  alerts,
  timeRange = 'day',
  chartType = 'bar',
  height = 300,
  className = ''
}: AlertTrendChartProps) {
  // Process alerts into time-based buckets
  const chartData = React.useMemo(() => {
    if (!alerts.length) return [];

    // Determine time bucket size
    const now = new Date();
    let bucketSize: number;
    let buckets: number;
    let formatLabel: (date: Date) => string;

    switch (timeRange) {
      case 'day':
        bucketSize = 60 * 60 * 1000; // 1 hour
        buckets = 24;
        formatLabel = (date) => date.getHours().toString().padStart(2, '0') + ':00';
        break;
      case 'week':
        bucketSize = 24 * 60 * 60 * 1000; // 1 day
        buckets = 7;
        formatLabel = (date) => date.toLocaleDateString('en-US', { weekday: 'short' });
        break;
      case 'month':
        bucketSize = 24 * 60 * 60 * 1000; // 1 day
        buckets = 30;
        formatLabel = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        break;
      default:
        bucketSize = 60 * 60 * 1000;
        buckets = 24;
        formatLabel = (date) => date.getHours().toString().padStart(2, '0') + ':00';
    }

    // Create time buckets
    const bucketData: AlertTrendData[] = [];
    for (let i = buckets - 1; i >= 0; i--) {
      const bucketStart = new Date(now.getTime() - i * bucketSize);
      bucketData.push({
        period: formatLabel(bucketStart),
        timestamp: bucketStart.getTime(),
        EARLY: 0,
        MID: 0,
        LATE: 0,
        total: 0,
      });
    }

    // Count alerts in each bucket
    alerts.forEach(alert => {
      const alertTime = new Date(alert.createdAt).getTime();
      const bucketIndex = Math.floor((now.getTime() - alertTime) / bucketSize);
      
      if (bucketIndex >= 0 && bucketIndex < buckets) {
        const bucket = bucketData[buckets - 1 - bucketIndex];
        bucket[alert.severity]++;
        bucket.total++;
      }
    });

    return bucketData;
  }, [alerts, timeRange]);

  // Custom tooltip formatter
  const formatTooltip = (value: number, name: string) => {
    return [value, `${name} Alerts`];
  };

  if (!chartData.length || chartData.every(d => d.total === 0)) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${className}`} style={{ height }}>
        <p className="text-gray-500">No alert data available</p>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {chartType === 'bar' ? (
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={formatTooltip} />
            <Legend />
            <Bar dataKey="EARLY" stackId="a" fill={SEVERITY_COLORS.EARLY} />
            <Bar dataKey="MID" stackId="a" fill={SEVERITY_COLORS.MID} />
            <Bar dataKey="LATE" stackId="a" fill={SEVERITY_COLORS.LATE} />
          </BarChart>
        ) : (
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={formatTooltip} />
            <Legend />
            <Line type="monotone" dataKey="EARLY" stroke={SEVERITY_COLORS.EARLY} strokeWidth={2} />
            <Line type="monotone" dataKey="MID" stroke={SEVERITY_COLORS.MID} strokeWidth={2} />
            <Line type="monotone" dataKey="LATE" stroke={SEVERITY_COLORS.LATE} strokeWidth={2} />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

// Alert summary widget
interface AlertSummaryProps {
  alerts: Alert[];
  className?: string;
}

export function AlertSummary({ alerts, className = '' }: AlertSummaryProps) {
  const summary = React.useMemo(() => {
    const counts = {
      EARLY: 0,
      MID: 0,
      LATE: 0,
      total: alerts.length,
      open: 0,
      acknowledged: 0,
    };

    alerts.forEach(alert => {
      counts[alert.severity]++;
      if (alert.status === 'OPEN') counts.open++;
      if (alert.status === 'ACKNOWLEDGED') counts.acknowledged++;
    });

    return counts;
  }, [alerts]);

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Alert Summary</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Total Alerts:</span>
            <span className="font-medium">{summary.total}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Open:</span>
            <span className="font-medium text-red-600">{summary.open}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Acknowledged:</span>
            <span className="font-medium text-yellow-600">{summary.acknowledged}</span>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-yellow-600">Early:</span>
            <span className="font-medium">{summary.EARLY}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-orange-600">Mid:</span>
            <span className="font-medium">{summary.MID}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-red-600">Late:</span>
            <span className="font-medium">{summary.LATE}</span>
          </div>
        </div>
      </div>
    </div>
  );
}